import React from "react";

import { Icon, Text, Heading } from "@telia/teddy";
import _compact from "lodash/compact";
import _get from "lodash/get";
import moment from "moment";
import { useSelector } from "react-redux";
import { RootState } from "../../../../../models";
import { isTransfer as isTransferSelector } from "../../../app/state/selectors";
import {
    getTransferBreakageFee,
    getSelectedPhoneNumber,
    getTransferNewLegalOwnerField,
    getTransferNewIndividualLegalOwnerFullName,
    getTransferDate,
    getTransferExistingSwitchAgreement,
    getTransferAgreementsToKeep,
    getTransferExistingInsurance,
    getTransferExistingSubscription,
    getTransferExistingServicesOnBAN,
    getTransferExistingLeaseAgreements,
} from "../../../checkout/state/selectors/accountSelectors";
import { getFormattedPhoneNumber } from "../../../rapidform/formValidation";
import Agreement from "../../components/molecules/Agreement/Agreement";
import InformationTable from "../../components/molecules/InformationTable/InformationTable";
import RelatedServices from "../../components/molecules/RelatedServices/RelatedServices";
import SubscriptionInformation from "../../components/molecules/SubscriptionInformation/SubscriptionInformation";
import TEXT from "../../text-consts";
import {
    convertAgreementToBoxData,
    getKeepAgreementDescription,
    isSwitchPlusInsurance,
    SCREEN_INSURANCE,
} from "../../utils";

const Ingress = () => {
    const transferDate = useSelector((state: RootState) => getTransferDate(state));
    const newLegalOwnerOganizationName = useSelector((state: RootState) =>
        getTransferNewLegalOwnerField(state, "organization", "name"),
    );
    const isNewLegalB2B = !!newLegalOwnerOganizationName;
    const newLegalOwnerIndividualFullName = useSelector((state: RootState) =>
        getTransferNewIndividualLegalOwnerFullName(state),
    );
    const selectedPhoneNumber = useSelector((state: RootState) => getSelectedPhoneNumber(state));
    const breakageFee = useSelector((state: RootState) => getTransferBreakageFee(state));
    const isTransfer = useSelector((state: RootState) => isTransferSelector(state));

    const data = [
        {
            label: "Overføres til:",
            value: isNewLegalB2B ? newLegalOwnerOganizationName : newLegalOwnerIndividualFullName,
        },
        { label: "Telefonnummer:", value: getFormattedPhoneNumber(selectedPhoneNumber) },
        {
            label: "Ønsket overføringsdato:",
            value: transferDate ? moment(transferDate).format("DD.MM.YYYY") : "Ingen dato angitt",
        },
    ];

    if (breakageFee) {
        data.push({ label: "Bruddgebyr:", value: `${breakageFee},-` });
    }

    return (
        <>
            <div
                className="paragraph"
                dangerouslySetInnerHTML={{
                    __html: TEXT.CONSENT_FORMER_LEGAL.REQUEST_RECEIVED(
                        isNewLegalB2B ? newLegalOwnerOganizationName : newLegalOwnerIndividualFullName,
                        getFormattedPhoneNumber(selectedPhoneNumber),
                    ),
                }}
                data-di-mask
            />
            {transferDate && (
                <div
                    className="paragraph"
                    dangerouslySetInnerHTML={{
                        __html: TEXT.CONSENT_FORMER_LEGAL.TRANSFER_DATE_INFO(moment(transferDate).format("DD.MM.YYYY")),
                    }}
                    data-di-mask
                />
            )}
            <InformationTable data={data} withBorder={true} withHr={false} data-di-mask />
            {isTransfer && (
                <Text mb="100" data-di-mask>
                    {TEXT.CONSENT_FORMER_LEGAL.RECEIVED_APPROVAL_TEXT(breakageFee)}
                </Text>
            )}
        </>
    );
};

const OrderCompleted = () => {
    const newLegalOwnerOganizationName = useSelector((state: RootState) =>
        getTransferNewLegalOwnerField(state, "organization", "name"),
    );
    const isNewLegalB2B = !!newLegalOwnerOganizationName;
    const newLegalOwnerIndividualFullName = useSelector((state: RootState) =>
        getTransferNewIndividualLegalOwnerFullName(state),
    );

    return (
        <>
            <Heading mb="100" as="h2" variant="title-200">
                {TEXT.CONSENT_FORMER_LEGAL.APPROVAL_HEADING}
            </Heading>
            <Text mb="100">{TEXT.CONSENT_FORMER_LEGAL.RECEIVED_APPROVAL}</Text>
            <Text mb="100" data-di-mask>
                {TEXT.CONSENT_FORMER_LEGAL.ORDER_CONFIRMATION_DESCRIPTION(
                    isNewLegalB2B ? newLegalOwnerOganizationName : newLegalOwnerIndividualFullName,
                )}
            </Text>
        </>
    );
};

const ExistingOwnerInformation = () => {
    const existingSwitchAgreement = useSelector((state: RootState) => getTransferExistingSwitchAgreement(state));
    const existingLeaseAgreements = useSelector((state: RootState) => getTransferExistingLeaseAgreements(state));
    const agreementsToKeep = useSelector((state: RootState) => getTransferAgreementsToKeep(state));
    const existingServicesOnBAN = useSelector((state: RootState) => getTransferExistingServicesOnBAN(state));
    const existingInsurance = useSelector((state: RootState) => getTransferExistingInsurance(state));
    const existingSubscription = useSelector((state: RootState) => getTransferExistingSubscription(state));

    const existingAgreements = _compact([existingSwitchAgreement, ...(existingLeaseAgreements || [])]);
    const existingAgreementsToKeep = existingAgreements.filter((agreement) => _get(agreementsToKeep, agreement.type));
    const existingAgreementsToTransfer = existingAgreements.filter(
        (agreement) => !_get(agreementsToKeep, agreement.type),
    );

    const hasAgreementsToKeep = existingAgreementsToKeep.length > 0;
    const hasAgreementsToTransfer = existingAgreementsToTransfer.length > 0;

    const isInsuranceSwitchPlus = isSwitchPlusInsurance(existingInsurance?.type);

    return (
        <>
            <SubscriptionInformation heading={TEXT.LANDING_PAGE.INFORMATION_HEADING.MAIN} showNewOwner />
            <RelatedServices services={existingServicesOnBAN} />
            {hasAgreementsToTransfer && (
                <Heading mb="100" as="h2" variant="title-100" className="consent-landing-page__related-phone-title">
                    {TEXT.CONSENT_FORMER_LEGAL.RELATED_PHONE}
                </Heading>
            )}

            <div>
                {existingAgreementsToTransfer.map((agreement, index) => (
                    <Agreement
                        key={index}
                        {...convertAgreementToBoxData(agreement)}
                        existingInsurance={existingInsurance}
                    />
                ))}
                {hasAgreementsToKeep && (
                    <div className="consent-landing-page__keep-agreements-container">
                        <Heading
                            mb="100"
                            as="h2"
                            variant="title-100"
                            className="consent-information__table__table-heading"
                        >
                            <div>
                                Avtaler som <u>ikke</u> overføres
                            </div>
                        </Heading>
                        <span>{TEXT.LANDING_PAGE.KEEP_AGREEMENT_DESCRIPTION}</span>
                        {existingAgreementsToKeep.map((agreement, index) => (
                            <Agreement
                                key={index}
                                {...convertAgreementToBoxData(agreement)}
                                existingInsurance={isInsuranceSwitchPlus ? SCREEN_INSURANCE : existingInsurance}
                                footer={
                                    <div className="consent-landing-page__keep-agreement-footer">
                                        <Icon name="info" />
                                        <p>
                                            {getKeepAgreementDescription(
                                                agreement.type,
                                                existingInsurance?.type,
                                                existingSubscription.newLegalOwner,
                                            )}
                                        </p>
                                    </div>
                                }
                            />
                        ))}
                    </div>
                )}
            </div>
        </>
    );
};

const InformationBlocks = {
    Ingress,
    OrderCompleted,
    ExistingOwnerInformation,
};

export default InformationBlocks;
